// Components/Pages/Admin/Stories.jsx
import { useState, useEffect } from "react";
import { api } from "./api";
import { FaPlus, FaEdit, FaTrash, FaTimes, FaSave, FaUser, FaCalendar, FaUpload } from "react-icons/fa";
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const Stories = () => {
  const [stories, setStories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingStory, setEditingStory] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [formData, setFormData] = useState({
    title: "",
    summary: "",
    full_story: "",
    author: "",
    position: "",
    date: "",
    image: ""
  });

  useEffect(() => {
    fetchStories();
  }, []);

  const fetchStories = async () => {
    try {
      const data = await api.getStories();
      setStories(data);
    } catch (error) {
      console.error('Error fetching stories:', error);
      toast.error('Failed to load stories');
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setEditingStory(null);
    setFormData({
      title: "",
      summary: "",
      full_story: "",
      author: "",
      position: "",
      date: new Date().toLocaleDateString('en-US', { month: 'long', year: 'numeric' }),
      image: ""
    });
    setShowForm(true);
  };

  const handleEdit = (story) => {
    setEditingStory(story);
    setFormData({
      title: story.title || "",
      summary: story.summary || "",
      full_story: story.full_story || "",
      author: story.author || "",
      position: story.position || "",
      date: story.date || new Date().toLocaleDateString('en-US', { month: 'long', year: 'numeric' }),
      image: story.image || ""
    });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this story?')) {
      const toastId = toast.loading('Deleting story...');
      try {
        await api.deleteStory(id);
        await fetchStories();
        toast.update(toastId, {
          render: 'Story deleted successfully!',
          type: 'success',
          isLoading: false,
          autoClose: 3000
        });
      } catch (error) {
        console.error('Error deleting story:', error);
        toast.update(toastId, {
          render: 'Failed to delete story',
          type: 'error',
          isLoading: false,
          autoClose: 3000
        });
      }
    }
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Please upload an image file');
      e.target.value = '';
      return;
    }

    setUploading(true);
    const toastId = toast.loading('Uploading image...');
    
    try {
      const result = await api.uploadFile(file);
      setFormData(prev => ({ ...prev, image: result.filepath }));
      
      toast.update(toastId, {
        render: 'Image uploaded successfully!',
        type: 'success',
        isLoading: false,
        autoClose: 3000
      });
    } catch (error) {
      console.error('Error uploading image:', error);
      toast.update(toastId, {
        render: 'Failed to upload image',
        type: 'error',
        isLoading: false,
        autoClose: 3000
      });
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.title.trim()) {
      toast.error('Please enter a title');
      return;
    }
    if (!formData.full_story.trim()) {
      toast.error('Please enter the story');
      return;
    }
    if (!formData.author.trim()) {
      toast.error('Please enter the author name');
      return;
    }
    if (!formData.position.trim()) {
      toast.error('Please enter the position');
      return;
    }

    const summary = formData.full_story.substring(0, 150) + (formData.full_story.length > 150 ? '...' : '');
    
    const dataToSend = {
      ...formData,
      summary: summary
    };

    const toastId = toast.loading(editingStory ? 'Updating story...' : 'Creating story...');

    try {
      if (editingStory) {
        await api.updateStory(editingStory.id, dataToSend);
        toast.update(toastId, {
          render: 'Story updated successfully!',
          type: 'success',
          isLoading: false,
          autoClose: 3000
        });
      } else {
        await api.createStory(dataToSend);
        toast.update(toastId, {
          render: 'Story created successfully!',
          type: 'success',
          isLoading: false,
          autoClose: 3000
        });
      }
      setShowForm(false);
      await fetchStories();
    } catch (error) {
      console.error('Error saving story:', error);
      toast.update(toastId, {
        render: 'Failed to save story',
        type: 'error',
        isLoading: false,
        autoClose: 3000
      });
    }
  };

  const closeForm = () => {
    setShowForm(false);
    setEditingStory(null);
  };

  if (loading) {
    return <div className="text-center py-8">Loading...</div>;
  }

  return (
    <div>
      <ToastContainer 
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />

      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-primaryBlue">Impact Stories</h2>
        <button
          onClick={handleAdd}
          className="bg-primaryGold text-primaryBlue px-4 py-2 rounded-lg font-semibold hover:bg-yellow-400 transition-all flex items-center gap-2"
        >
          <FaPlus /> Add Story
        </button>
      </div>

      {/* Stories List */}
      <div className="bg-white rounded-xl shadow-md overflow-hidden">
        {stories.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-500">No stories yet.</p>
            <p className="text-gray-400 text-sm mt-1">Click "Add Story" to create one.</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {stories.map((story) => (
              <div key={story.id} className="px-6 py-4 hover:bg-gray-50 transition-colors">
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0">
                    {story.image ? (
                      <img 
                        src={`https://api.rigokenya.org${story.image}`}
                        alt={story.author}
                        className="w-16 h-16 rounded-full object-cover border-2 border-primaryGold"
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = 'https://via.placeholder.com/64/1a365d/ffffff?text=User';
                        }}
                      />
                    ) : (
                      <div className="w-16 h-16 rounded-full bg-gray-200 flex items-center justify-center text-gray-400 border-2 border-primaryGold">
                        <FaUser className="text-2xl" />
                      </div>
                    )}
                  </div>
                  
                  <div className="flex-1">
                    <h3 className="font-bold text-primaryBlue text-lg">{story.title}</h3>
                    
                    <div className="flex flex-wrap items-center gap-2 mt-1 text-sm">
                      <span className="font-semibold text-gray-800">{story.author}</span>
                      {story.position && (
                        <span className="text-gray-500">• {story.position}</span>
                      )}
                    </div>
                    
                    {story.date && (
                      <div className="flex items-center gap-1 mt-1 text-xs text-gray-400">
                        <FaCalendar className="text-xs" />
                        <span>{story.date}</span>
                      </div>
                    )}
                    
                    <details className="mt-2">
                      <summary className="text-xs text-primaryGold cursor-pointer hover:text-yellow-600">
                        Read Full Story
                      </summary>
                      <p className="text-gray-600 mt-2 text-sm p-3 bg-gray-50 rounded-lg">
                        {story.full_story}
                      </p>
                    </details>
                  </div>
                  
                  <div className="flex items-center gap-2 ml-4 flex-shrink-0">
                    <button
                      onClick={() => handleEdit(story)}
                      className="text-blue-500 hover:text-blue-700 transition-colors p-2"
                      title="Edit"
                    >
                      <FaEdit />
                    </button>
                    <button
                      onClick={() => handleDelete(story.id)}
                      className="text-red-500 hover:text-red-700 transition-colors p-2"
                      title="Delete"
                    >
                      <FaTrash />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Add/Edit Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center p-6 border-b border-gray-200 sticky top-0 bg-white z-10">
              <h2 className="text-xl font-bold text-primaryBlue">
                {editingStory ? 'Edit Story' : 'Add New Story'}
              </h2>
              <button
                onClick={closeForm}
                className="text-gray-500 hover:text-gray-700 transition-colors"
              >
                <FaTimes className="text-xl" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Profile Image
                </label>
                <div className="flex items-center gap-4">
                  <div className="flex-shrink-0">
                    {formData.image ? (
                      <img 
                        src={`https://api.rigokenya.org${formData.image}`}
                        alt="Profile"
                        className="w-20 h-20 rounded-full object-cover border-2 border-primaryGold"
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = 'https://via.placeholder.com/80/1a365d/ffffff?text=User';
                        }}
                      />
                    ) : (
                      <div className="w-20 h-20 rounded-full bg-gray-200 flex items-center justify-center text-gray-400 border-2 border-primaryGold">
                        <FaUser className="text-3xl" />
                      </div>
                    )}
                  </div>
                  <div className="relative flex-1">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleImageUpload}
                      className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                      disabled={uploading}
                    />
                    <button 
                      type="button"
                      className="w-full bg-gray-100 text-gray-700 px-4 py-2 rounded-lg border-2 border-dashed border-gray-300 hover:border-primaryGold transition-all flex items-center justify-center gap-2"
                      disabled={uploading}
                    >
                      <FaUpload />
                      {uploading ? 'Uploading...' : formData.image ? 'Change Image' : 'Upload Image'}
                    </button>
                    <p className="text-xs text-gray-400 mt-1">Upload a profile photo (will be displayed as circle)</p>
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Title <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="title"
                  value={formData.title}
                  onChange={handleFormChange}
                  placeholder="e.g., My Journey in Tech"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primaryGold"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Story/Testimonial <span className="text-red-500">*</span>
                </label>
                <textarea
                  name="full_story"
                  value={formData.full_story}
                  onChange={handleFormChange}
                  placeholder="Write the story/testimonial here..."
                  rows={6}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primaryGold"
                  required
                />
                <p className="text-xs text-gray-400 mt-1">The full story or testimonial from the person</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="author"
                  value={formData.author}
                  onChange={handleFormChange}
                  placeholder="e.g., Latasha Ndirangu"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primaryGold"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Position/Role <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="position"
                  value={formData.position}
                  onChange={handleFormChange}
                  placeholder="e.g., Software Development Alumnus"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primaryGold"
                  required
                />
                <p className="text-xs text-gray-400 mt-1">e.g., Student, Alumnus, Community Member</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Date
                </label>
                <input
                  type="text"
                  name="date"
                  value={formData.date}
                  onChange={handleFormChange}
                  placeholder="e.g., January 2024"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primaryGold"
                />
                <p className="text-xs text-gray-400 mt-1">Auto-set to current date if left blank</p>
              </div>

              <div className="flex gap-3 pt-4 border-t border-gray-200">
                <button
                  type="submit"
                  className="flex-1 bg-primaryGold text-primaryBlue px-4 py-2 rounded-lg font-semibold hover:bg-yellow-400 transition-all flex items-center justify-center gap-2"
                >
                  <FaSave />
                  {editingStory ? 'Update Story' : 'Publish Story'}
                </button>
                <button
                  type="button"
                  onClick={closeForm}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg font-semibold hover:bg-gray-50 transition-all"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Stories;